import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CgSortAz } from 'react-icons/cg';
import './DashboardTabs.scss';
import { Breadcrumb, Form } from 'react-bootstrap';
import useHasPermission from '../../../core/hooks/useHasPermission';
import { GraphLayoutType, startYear } from '../../../core/constants/common';
import {
  getCompanyInfoFromStorage,
  getDefaultSelectableYear,
  getMaxSelectableYear,
  getYearDropdownLabel,
  isFinancialYearReporting,
} from '../../../core/utilities/assessmentPeriodUtils';

const DashboardTabs = ({
  onOpenWidgetLibrary,
  selectedCategory,
  selectedYear,
  onCategoryChange,
  onYearChange,
  onToggleKpiFilter,
  isKpiFilterVisible,
  // New props for embedded mode
  isEmbedded = false,
  selectedScope,
  onScopeChange,
  breadcrumbParentLabel
}) => {
  const { hasDashboardUpdatePermission,hasGoalsAndKPIsViewPermission } = useHasPermission();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const activeTab = searchParams.get('tab') || (isEmbedded ? 'overview' : 'esg');
  const companyInfo = getCompanyInfoFromStorage();
  const isFinancialYear = isFinancialYearReporting(companyInfo);
  const currentYearValue = new Date().getFullYear();
  const maxSelectableYear = getMaxSelectableYear();
  const startYearValue = isEmbedded ? startYear : currentYearValue - 5;

  // Initialize from localStorage on mount to persist selected values across page refreshes
  useEffect(() => {
    // Check localStorage for saved category and update if different from current prop
    const savedCategory = localStorage.getItem('selectedCategory');
    if (savedCategory && savedCategory !== selectedCategory && onCategoryChange) {
      onCategoryChange(savedCategory);
    }

    // Check localStorage for saved year and update if different from current prop
    let savedYear;
    if (activeTab === 'overview' && isEmbedded) {
      savedYear = localStorage.getItem('selectedOverviewYear');
    } else {
      savedYear = localStorage.getItem('selectedYear');
    }

    if (savedYear) {
      const parsedYear = parseInt(savedYear, 10);
      if (
        !Number.isNaN(parsedYear) &&
        parsedYear >= startYearValue &&
        parsedYear <= maxSelectableYear &&
        parsedYear !== selectedYear &&
        onYearChange
      ) {
        onYearChange(parsedYear);
      }
    } else if (onYearChange) {
      const defaultYear = getDefaultSelectableYear(companyInfo);
      if (defaultYear !== selectedYear) {
        onYearChange(defaultYear);
      }
    }
  }, []); // Run only on mount

  // Define tabs with translation keys
  const TABS = isEmbedded ? [
    { key: 'overview', label: t('Overview.label') }
  ] : [
    { key: 'esg', label: t('ESGPerformance.label') },
    ...(hasGoalsAndKPIsViewPermission() ? [{ key: 'goals', label: t('GoalsandKPIs.label') }] : []),
    { key: 'compare', label: t('ESGAnalytics.unitWisePerformance.label') },
    // { key: 'double', label: t('DoubleMateriality.label') },
  ];

  const handleTabClick = (key) => {
    const params = new URLSearchParams(searchParams);
    params.set('tab', key);
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  const handleCategoryChange = (e) => {
    localStorage.setItem('selectedCategory', e.target.value);
    onCategoryChange(e.target.value);
  };

  const handleYearChange = (e) => {
    if(activeTab === 'overview' && isEmbedded){
      localStorage.setItem('selectedOverviewYear', e.target.value);
    } else {
      localStorage.setItem('selectedYear', e.target.value);
    }
    onYearChange(parseInt(e.target.value));
  };

  const handleScopeChange = (e) => {
    if (onScopeChange) {
      onScopeChange(e.target.value);
    }
  };

  // Period range text for ESG tab (same logic as HomePage - FY vs calendar year)
  const periodRangeText = useMemo(() => {
    const y = Number(selectedYear) || currentYearValue;
    const fyStart = companyInfo?.financialYearStart ?? 'APRIL';
    const fyEnd = companyInfo?.financialYearEnd ?? 'MARCH';
    if (isFinancialYear) {
      const startMonthKey = `${fyStart.charAt(0)}${fyStart.slice(1).toLowerCase()}.label`;
      const endMonthKey = `${fyEnd.charAt(0)}${fyEnd.slice(1).toLowerCase()}.label`;
      return `${t(startMonthKey)} ${y - 1} ${t('to.label')} ${t(endMonthKey)} ${y}`;
    }
    return `${t('January.label')} ${y} ${t('to.label')} ${t('December.label')} ${y}`;
  }, [selectedYear, isFinancialYear, currentYearValue, t, companyInfo?.financialYearStart, companyInfo?.financialYearEnd]);

  const yearOptions = useMemo(() => {
    const options = [];
    for (let i = maxSelectableYear; i >= startYearValue; i--) {
      options.push(i);
    }
    return options;
  }, [maxSelectableYear, startYearValue]);

  return (
    <div
      className={`dashboard-tabs${isEmbedded ? ' dashboard-tabs--embedded' : ''}`}
      data-testid="dashboard-tabs"
    >
      <div className="dashboard-tabs__container">
        {/* Left side - Tabs */}
        <div className="dashboard-tabs__tablist border-0">
        {!isEmbedded ? <>{TABS.map(tab => (
            <button
              key={tab.key}
              className={`dashboard-tabs__tab${activeTab === tab.key ? ' dashboard-tabs__tab--active' : ''}`}
              onClick={() => handleTabClick(tab.key)}
            >
              {tab.label}
            </button>
          ))}</> :
          <><div className="List_header pb-0">
          <Breadcrumb className='h5'>
            <Breadcrumb.Item >{breadcrumbParentLabel || t("GHGEmissions.label")}</Breadcrumb.Item>
            <Breadcrumb.Item active>{t("Overview.label")}</Breadcrumb.Item>
          </Breadcrumb>
        </div></>}
        </div>

        {/* Right side - Controls */}
        <div className="dashboard-tabs__controls">
          {activeTab === 'overview' && isEmbedded && (
            <>
              {/* Scope Selector for embedded mode */}
              <select
                aria-label="scope-selector"
                id="scope-selector"
                className="form-select w-auto"
                value={selectedScope}
                onChange={handleScopeChange}
                autoComplete="off"
              >
                <option value={GraphLayoutType.OVERVIEW.name}>{t('Overview.label')}</option>
                <option value={GraphLayoutType.SCOPE_1.name}>{t('Scope1.label')}</option>
                <option value={GraphLayoutType.SCOPE_2.name}>{t('Scope2.label')}</option>
                <option value={GraphLayoutType.SCOPE_3.name}>{t('Scope3.label')}</option>
              </select>

              <select
                aria-label="year-selector"
                id="year-selector"
                className="form-select w-auto"
                value={selectedYear}
                onChange={handleYearChange}
                autoComplete="off"
              >
                {yearOptions.map(year => (
                  <option key={year} value={year}>{getYearDropdownLabel(year, isFinancialYear)}</option>
                ))}
              </select>

              {/* Add Widget Button */}
              <button
                hidden={!hasDashboardUpdatePermission()}
                className="btn btn-primary text-nowrap"
                onClick={onOpenWidgetLibrary}
              >
                + {t('AddWidgets.label')}
              </button>
            </>
          )}

          {activeTab === 'esg' && !isEmbedded && (
            <>
              {/* Period range (FY or calendar year, same as HomePage) */}
              <div className="dashboard-tabs__period-range">
                <span className="dashboard-tabs__period-label">{t('Period.label')}:</span>
                <span className="dashboard-tabs__period-value">{periodRangeText}</span>
              </div>

              {/* Category Selector */}

              <select
                aria-label="category-selector"
                id="category-selector"
                className="form-select"
                value={selectedCategory}
                onChange={handleCategoryChange}
                autoComplete="off"
              >
                {/* <option value="ALL">{t('All.label')}</option> */}
                <option value="Environmental">{t('Environmental.label')}</option>
                <option value="Social">{t('Social.label')}</option>
                <option value="Governance">{t('Governance.label')}</option>
              </select>

              <select
                aria-label="year-selector"
                id="year-selector"
                className="form-select"
                value={selectedYear}
                onChange={handleYearChange}
                autoComplete="off"
              >
                {yearOptions.map(year => (
                  <option key={year} value={year}>{getYearDropdownLabel(year, isFinancialYear)}</option>
                ))}
              </select>

              {/* Add Widget Button */}
              <button
                hidden={!hasDashboardUpdatePermission()}
                className="btn btn-primary text-nowrap"
                onClick={onOpenWidgetLibrary}
              >
                + {t('AddWidgets.label')}
              </button>
            </>
          )}

          {/* Filter Button - Always visible and aligned right */}
          {activeTab === 'goals' && (
            <button
              className="btn btn-outline-primary Goals_Kpis_Filter_btn"
              onClick={onToggleKpiFilter}
            >
              <CgSortAz /> {t("Filter.label")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardTabs; 